import React, { useEffect, useRef, useState } from 'react';
import { round } from 'lodash';
import { Box, Text, TextInput, Grid, Tip } from 'grommet';
import { getSuggestions } from '../../components/item-info';
import ItemTagSelector from '../../components/item-tag-selector';
import UserSelector from '../../components/user-selector';
import {
  FTSelect,
  LabeledInput,
  FTTextInput,
  FTTagInput,
  FTTextArea
} from '../../components/inputs';
import * as itemTypes from '../../items/item-types';
import DeliveryCaps from '../cap-strategy/delivery-caps.jsx';
import { AdvertiserInput } from '../advertiser-input';
import { EPCOverride } from '../epc-override';
import PrePingIntegrations from '../pre-ping-integrations';
import VerticalSelector from '../../components/vertical-selector.jsx';
import CampaignTypeSelector from '../campaign-type-selector.jsx';
import CustomerSuppressionSelector from '../customer-suppression-selector.jsx';

const CAMPAIGN_GOALS = [
  {
    name: 'CPA',
    type: 'CPA'
  },
  {
    name: 'CPC',
    type: 'CPC'
  },
  {
    name: 'CPM',
    type: 'CPM'
  },
  {
    name: 'CPI',
    type: 'CPI'
  },
  {
    name: 'CPE',
    type: 'CPE'
  }
];

export const TEMPORARY_SUPPRESSION = 'temporary';
const SUPPRESSION_OPTIONS = [
  {
    label: 'Never',
    value: 'permanent'
  },
  {
    label: 'After',
    value: TEMPORARY_SUPPRESSION
  }
];

// Avoids float noise from decimal * 100 (e.g. 0.07 -> 7.000000000000001).
const decimalToPercent = decimal => (decimal ? round(decimal * 100, 9) : '');

const CampaignInfo = props => {
  const { item, modifyItem, canEdit, addError } = props;
  const flowIds = Array.isArray(item.flowIds) ? item.flowIds : [];
  const [suggestions, setSuggestions] = useState([]);
  const [estimatedFulfillmentCostPercent, setEstimatedFulfillmentCostPercent] =
    useState(decimalToPercent(item.estimatedFulfillmentCost));
  // Holds the raw in-progress typed string, so it can't be purely derived
  // from item; resyncs only when item changes for some other reason (e.g.
  // Schedule Mode's projection).
  const lastPushedFulfillmentCostRef = useRef(item.estimatedFulfillmentCost);
  useEffect(() => {
    if (
      item.estimatedFulfillmentCost !== lastPushedFulfillmentCostRef.current
    ) {
      setEstimatedFulfillmentCostPercent(
        decimalToPercent(item.estimatedFulfillmentCost)
      );
      lastPushedFulfillmentCostRef.current = item.estimatedFulfillmentCost;
    }
  }, [item.estimatedFulfillmentCost]);

  return (
    <Box fill overflow={{ vertical: 'auto' }} pad='medium'>
      <Grid
        columns={['repeat(3, minmax(250px, 1fr))']}
        gap='medium'
        justifyContent='start'
      >
        <Box gap='medium'>
          <FTTextInput
            label='Name'
            disabled={!canEdit}
            value={item.name || ''}
            onChange={newValue => modifyItem({ name: newValue })}
          />
          <Tip content='This is the name external partners will use in campaign tracking.'>
            <Box>
              <FTTextInput
                label='External Campaign Name'
                disabled={!canEdit}
                value={item.externalName || ''}
                onChange={newValue => modifyItem({ externalName: newValue })}
              />
            </Box>
          </Tip>
          <FTTagInput
            label='Tags'
            disabled={!canEdit}
            tags={item.categories}
            onChange={newValue => modifyItem({ categories: newValue })}
            suggestions={suggestions}
            onTextChange={text =>
              getSuggestions(text, item.categories, setSuggestions)
            }
          />
          <CampaignTypeSelector
            campaignType={item.campaignType}
            disabled={!canEdit}
            modifyItem={modifyItem}
            addError={addError}
          />
          <CustomerSuppressionSelector
            emailSuppressionListId={item.emailSuppressionListId}
            advertiserId={item.advertiserId}
            disabled={!canEdit}
            modifyItem={modifyItem}
            addError={addError}
          />
        </Box>
        <FTTextArea
          height='100%'
          label='Description'
          disabled={!canEdit}
          value={item.description || ''}
          onChange={newValue => {
            modifyItem({ description: newValue });
          }}
        />

        <Box gap='medium'>
          <UserSelector
            itemUserName={item.accountManagerName}
            itemUserId={item.accountManagerUserId}
            onChange={({ id }) =>
              modifyItem({
                accountManagerUserId: id
              })
            }
            userRole='account_manager'
            label='Campaign Manager'
            addError={addError}
            disabled={!canEdit}
          />
          <AdvertiserInput
            item={item}
            canEdit={canEdit}
            modifyItem={modifyItem}
          />

          <FTSelect
            label='Goal'
            value={CAMPAIGN_GOALS.find(cg => cg.type === item.goal)}
            options={CAMPAIGN_GOALS}
            labelKey='name'
            valueKey='type'
            disabled={!canEdit}
            onChange={newValue => {
              modifyItem({ goal: newValue.type });
            }}
          />
          <FTTextInput
            disabled={!canEdit}
            label='Conversion Lookback Window (days)'
            type='number'
            min={1}
            max={31}
            value={item.lookBackWindowInDays ?? 7}
            title='Metric calculation lookback window in 1 to 31 days. Default is 7 days.'
            onChange={value => {
              modifyItem({
                lookBackWindowInDays: value === '' ? '' : parseInt(value)
              });
            }}
            tipContent='Metric calculation lookback window in 1 to 31 days. Default is 7 days.'
            onBlur={e => {
              const newValue = e.target.value;
              if (newValue < 1 || newValue > 31) {
                modifyItem({ lookBackWindowInDays: 7 });
              }
            }}
          />
          <VerticalSelector
            disabled={!canEdit}
            verticalId={item.verticalId}
            subVerticalId={item.subVerticalId}
            onChange={modifyItem}
            addError={addError}
          />
        </Box>
        <Box
          margin={{ vertical: 'medium' }}
          border={{ side: 'top', color: 'border400' }}
          gridArea='auto/span 3'
        />

        {/* Flows */}
        <Box gridArea='auto/span 3'>
          <LabeledInput label='Flows'>
            <ItemTagSelector
              itemType={itemTypes.FLOW}
              itemIds={flowIds}
              disabled={!canEdit}
              onItemAdd={itemId => {
                const newFlowIds = [...flowIds];
                newFlowIds.push(itemId);
                modifyItem({ flowIds: newFlowIds });
              }}
              onItemRemove={itemId => {
                const newFlowIds = [...flowIds];
                newFlowIds.splice(newFlowIds.indexOf(itemId), 1);
                modifyItem({ flowIds: newFlowIds });
              }}
            />
          </LabeledInput>
        </Box>

        {/* Scrub Rate */}
        <FTTextInput
          disabled={!canEdit}
          label='Scrub Rate (%)'
          type='number'
          min={0}
          value={item.scrubRate}
          title='Certain % of revenue that will be scrubbed by the advertiser.'
          onChange={newValue => {
            if (newValue === '' || isNaN(parseFloat(newValue))) {
              modifyItem({ scrubRate: null });
            } else {
              modifyItem({ scrubRate: newValue });
            }
          }}
        />

        {/* EPC Manual Override*/}
        <EPCOverride onChange={modifyItem} item={item} canEdit={canEdit} />

        {/* Credit Delay (min) */}
        <FTTextInput
          disabled={!canEdit}
          label='Credit Delay (min)'
          type='number'
          value={item.creditDelay || ''}
          title='Delay in minutes before a user will receive credit for converting this Campaign.'
          onChange={newValue => {
            modifyItem({ creditDelay: parseInt(newValue, 10) });
          }}
        />
        {/* Estimated Fulfillment Cost (%) */}
        <FTTextInput
          disabled={!canEdit}
          label='Estimated Fulfillment Cost (%)'
          type='number'
          min={0}
          max={100}
          step={0.01}
          value={estimatedFulfillmentCostPercent}
          title='Estimated Fulfillment Cost (%)'
          onChange={newValue => {
            const percentValue = parseFloat(newValue);
            setEstimatedFulfillmentCostPercent(newValue);

            if (newValue === '' || isNaN(percentValue)) {
              lastPushedFulfillmentCostRef.current = null;
              modifyItem({ estimatedFulfillmentCost: null });
            } else {
              const clampedPercent = Math.max(0, Math.min(100, percentValue));
              const decimalValue = clampedPercent / 100;
              lastPushedFulfillmentCostRef.current = decimalValue;
              modifyItem({ estimatedFulfillmentCost: decimalValue });
            }
          }}
          onBlur={e => {
            const value = parseFloat(e.target.value);
            if (!isNaN(value)) {
              const clampedValue = Math.max(0, Math.min(100, value));
              setEstimatedFulfillmentCostPercent(clampedValue);
              lastPushedFulfillmentCostRef.current = clampedValue / 100;
              modifyItem({ estimatedFulfillmentCost: clampedValue / 100 });
            }
          }}
        />

        {/* Allow Repeat Conversions */}
        <Box gap='xsmall' gridArea='5/2' direction='row'>
          <Box style={{ width: '180px' }}>
            <Text
              margin={{
                top: 'small'
              }}
            >
              Allow Repeat Conversions
            </Text>
          </Box>
          <Box gap='xsmall'>
            <FTSelect
              style={{ width: '100px' }}
              disabled={!canEdit}
              value={SUPPRESSION_OPTIONS.find(
                cg => cg.value === item.suppressionType
              )}
              options={SUPPRESSION_OPTIONS}
              labelKey='label'
              valueKey='value'
              onChange={newValue =>
                modifyItem({ suppressionType: newValue.value })
              }
            />
            {item.suppressionType === TEMPORARY_SUPPRESSION && (
              <Box direction='row' align='center'>
                <TextInput
                  min={1}
                  max={365}
                  type='number'
                  disabled={!canEdit}
                  style={{ width: '90px' }}
                  value={item?.repeatConversionAfterDays ?? 0}
                  onChange={e =>
                    modifyItem({ repeatConversionAfterDays: e.target.value })
                  }
                />
                <Text
                  margin={{
                    left: 'xsmall'
                  }}
                >
                  Days
                </Text>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          margin={{ vertical: 'medium' }}
          border={{ side: 'top', color: 'border400' }}
          gridArea='auto/span 3'
        />

        <Box direction='row'>
          <DeliveryCaps
            onChange={changes => {
              modifyItem(changes);
              // Auto-disable budget optimization when prerequisites are no longer met
              if (item.automatedBudgetOptimization) {
                const newCapStrategy =
                  changes.capStrategy !== undefined
                    ? changes.capStrategy
                    : item.capStrategy;
                const newDailyRevenueCap =
                  changes.dailyRevenueCap !== undefined
                    ? changes.dailyRevenueCap
                    : item.dailyRevenueCap;
                if (
                  newCapStrategy !== 'revenue' ||
                  !newDailyRevenueCap ||
                  newDailyRevenueCap <= 0
                ) {
                  modifyItem({ automatedBudgetOptimization: false });
                }
              }
            }}
            caps={{
              dailyConversionCap: item.dailyConversionCap,
              dailyRevenueCap: item.dailyRevenueCap,
              monthlyConversionCap: item.monthlyConversionCap,
              monthlyRevenueCap: item.monthlyRevenueCap,
              overallConversionCap: item.overallConversionCap,
              overallRevenueCap: item.overallRevenueCap,
              capStrategy: item.capStrategy
            }}
            counts={item.capStats || {}}
            isDirty={item.isDirty}
            item={item}
            modifyItem={modifyItem}
            canEdit={canEdit}
          />
        </Box>
        <Box gap='small'>
          <FTTextInput
            placeholder='Enter CPA Goal Amount'
            type='number'
            step={0.01}
            min={0}
            label='Campaign CPA Goal'
            icon={<Text>$</Text>}
            disabled={!canEdit}
            value={item.campaignCPAGoalAmount}
            onChange={val => {
              const amount = val ? parseFloat(val) : null;
              modifyItem({
                campaignCPAGoalAmount: amount,
                campaignCPAGoalType: amount ? item.campaignCPAGoalType : null
              });
            }}
          />
          <FTSelect
            label='Campaign CPA Goal Type'
            placeholder='Firm or Soft'
            labelKey='name'
            valueKey='type'
            options={[
              { name: 'Firm', type: 'Firm' },
              { name: 'Soft', type: 'Soft' }
            ]}
            value={item.campaignCPAGoalType}
            onChange={opt => modifyItem({ campaignCPAGoalType: opt.type })}
            disabled={!canEdit || !item.campaignCPAGoalAmount}
          />
          <FTSelect
            data-testid='campaignCPAEvent'
            label='Campaign CPA Event'
            placeholder='Select conversion event'
            labelKey='displayName'
            valueKey='id'
            options={item.conversionMappings || []}
            value={
              (item.conversionMappings || []).find(
                c => c.id === item.campaignCPAEventId
              ) || ''
            }
            onChange={opt =>
              modifyItem({ campaignCPAEventId: opt?.id ?? null })
            }
            disabled={!canEdit}
            clear={{ label: 'Clear selection' }}
          />
        </Box>

        <Box
          margin={{ vertical: 'medium' }}
          border={{ side: 'top', color: 'border400' }}
          gridArea='auto/span 3'
        />

        <Box margin={{ bottom: 'large' }}>
          <PrePingIntegrations
            item={item}
            canEdit={canEdit}
            modifyItem={modifyItem}
            addError={addError}
          />
        </Box>
      </Grid>
    </Box>
  );
};

CampaignInfo.displayName = 'CampaignInfo';

export default CampaignInfo;
